import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext'; 
import { Save, ArrowLeft, Key, Mail, ShieldCheck, MapPin, User, Building2, Users, CalendarClock } from 'lucide-react';

const InputField = ({ label, value, onChange, type = "text", disabled = false, icon: Icon }) => (
  <div className="flex flex-col border-b border-gray-100 py-1">
    <label className="text-[0.9em] text-gray-400 italic font-black uppercase mb-1">{label}</label>
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-gray-300" />}
      <input 
        type={type} 
        value={value || ''} 
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full outline-none font-black text-blue-900 text-[12px] bg-transparent uppercase placeholder:italic placeholder:font-normal disabled:opacity-50"
        placeholder={`ENTER ${label}...`}
      />
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white border-2 border-gray-200 mb-6 rounded-sm shadow-sm">
    <div className="bg-gray-50 p-2 border-b font-black text-[#005495] italic uppercase text-[1.0em] tracking-widest">{title}</div>
    <div className="p-6 grid grid-cols-2 gap-x-12 gap-y-4">{children}</div>
  </div>
);

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const isNew = !id || id === 'new' || id === 'undefined';
  
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [sites, setSites] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // 🎯 لملء قائمة التفويض

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [userData, setUserData] = useState({
    username: '', full_name: '', email: '', password: '',
    role_id: '', site_id: '', org_id: '', is_active: true, language: 'EN',
    additional_roles: [], // 🎯 رولات إضافية
    delegation: { delegate_user_id: '', start_date: '', end_date: '' } // 🎯 بيانات التفويض
  });

  const fetchOrgs = async () => {
      const res = await axios.get('/api/organizations');
      setOrgs(res.data);
  };

  useEffect(() => {
      fetchOrgs();
      if (userData.org_id) {
          axios.get(`/api/sites/${userData.org_id}`)
              .then(res => {
                  const data = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
                  setSites(data);
              });
      }
  }, [userData.org_id]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
  
      try {
        const rolesRes = await axios.get('/api/roles', { headers });
        setRoles(rolesRes.data);

        // جلب قائمة المستخدمين للتفويض (بدون pagination)
        const usersRes = await axios.get('/api/users?limit=500', { headers });
        setAllUsers(usersRes.data.data || []);
  
        if (isNew) {
          setUserData(prev => ({ ...prev }));
          return; 
        }
  
        setLoading(true);
        const userRes = await axios.get(`/api/users/${id}`, { headers });
        setUserData({ 
            ...userRes.data, 
            password: '',
            additional_roles: userRes.data.additional_roles || [],
            delegation: userRes.data.delegation || { delegate_user_id: '', start_date: '', end_date: '' }
        });
      } catch (err) {
        console.error("Fetch Error:", err);
        if (!isNew) toast.error("User not found");
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [id, isNew]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // تنظيف بيانات التفويض لو مش كاملة
      let payload = { ...userData };
      if (!payload.delegation.delegate_user_id || !payload.delegation.start_date || !payload.delegation.end_date) {
          payload.delegation = null;
      }

      if (isNew) {
        await axios.post('/api/users', payload, { headers });
        toast.success("User created successfully");
      } else {
        await axios.put(`/api/users/${id}`, payload, { headers });
        toast.success("User updated successfully");
      }
      navigate('/users');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving user");
    } finally { setLoading(false); }
  };

  // دالة للتحكم في تحديد وإلغاء الرولات الإضافية
  const toggleAdditionalRole = (roleId) => {
      setUserData(prev => {
          const currentRoles = prev.additional_roles;
          if (currentRoles.includes(roleId)) {
              return { ...prev, additional_roles: currentRoles.filter(id => id !== roleId) };
          } else {
              return { ...prev, additional_roles: [...currentRoles, roleId] };
          }
      });
  };

  return (
    <div className="flex flex-col h-full bg-[#f3f4f6] font-bold uppercase tracking-tighter" style={{ fontSize: `${fontSize}px` }}>
      {/* Header Bar */}
      <div className="bg-white p-3 border-b shadow-sm flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center gap-4 ml-4">
          <button onClick={() => navigate('/users')} className="p-1 hover:bg-gray-100 rounded-full transition-all text-blue-900" >
            <ArrowLeft size={18} { ...(isRTL && { className: 'rotate-180' }) } />
          </button>
          <div className="flex flex-col">
            <span className="text-[9px] text-gray-400 leading-none italic">{t('userDetails.titleEdit')}</span>
            <span className="text-blue-900 font-black text-lg leading-none">{userData.username || t('userDetails.titleNew')}</span>
          </div>
        </div>
        <button 
          onClick={handleSave} disabled={loading}
          className="bg-[#005495] text-white px-10 py-2.5 rounded-sm flex items-center gap-2 hover:bg-black transition-all mr-4 shadow-lg active:scale-95"
        >
          <Save size={16} /> {loading ? t('userDetails.saving') : t('userDetails.btnSave')}
        </button>
      </div>

      <div className="flex-1 overflow-auto p-8 max-w-5xl mx-auto w-full">
        {/* 1. Identification Section */}
        <Section title={t('userDetails.secIdent')}>
          <InputField label={t('userDetails.lblLoginId')} value={userData.username} onChange={v => setUserData({...userData, username: v})} disabled={!isNew} icon={User} />
          <InputField label={t('userDetails.lblFullName')} value={userData.full_name} onChange={v => setUserData({...userData, full_name: v})} />
          <InputField label={t('userDetails.lblDisplayName')} value={userData.display_name} onChange={v => setUserData({...userData, display_name: v})} />
          <InputField label={t('userDetails.lblEmail')} value={userData.email} onChange={v => setUserData({...userData, email: v.toLowerCase()})} icon={Mail} type="email" />
          <InputField label={t('userDetails.lblPassword')} value={userData.password} onChange={v => setUserData({...userData, password: v})} type="password" icon={Key} />
          
          <div className="flex items-center gap-4 py-2 border-b border-gray-100">
             <label className="text-[9px] text-gray-400 italic font-black uppercase">{t('userDetails.lblActive')}</label>
             <input type="checkbox" checked={userData.is_active} onChange={e => setUserData({...userData, is_active: e.target.checked})} className="w-4 h-4 cursor-pointer accent-[#005495]" />
          </div>
        </Section>

        {/* 2. Access Control Section */}
        <Section title={t('userDetails.secAccess')}>
          <div className="flex flex-col border-b border-gray-100 py-1">
            <label className="text-[9px] text-gray-400 italic font-black uppercase mb-1">{t('userDetails.lblPrimaryRole')}</label>
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-orange-400" />
              <select 
                value={userData.role_id} 
                onChange={e => setUserData({...userData, role_id: e.target.value})}
                className="w-full bg-transparent outline-none font-black text-blue-900 text-[12px] uppercase cursor-pointer"
              >
                <option value="">-- SELECT PRIMARY ROLE --</option>
                {roles.map(r => <option key={r.role_id} value={r.role_id}>{r.name}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col border-b border-gray-300 group">
              <label className="text-[9px] font-black text-[#005495] uppercase tracking-tighter flex items-center gap-1 mb-1">
                  <Building2 size={10}/> {t('userDetails.lblDefOrg')}
              </label>
              <select 
                  className="outline-none py-1 font-black text-blue-900 bg-transparent uppercase text-[11px]"
                  value={userData.org_id}
                  onChange={e => setUserData({...userData, org_id: e.target.value, site_id: ''})}
              >
                  <option value="">Select Org...</option>
                  {orgs.map(org => <option key={org.org_id} value={org.org_id}>{org.description}</option>)}
              </select>
          </div>

          <div className="flex flex-col border-b border-gray-300 group">
              <label className="text-[0.9em] font-black text-[#005495] uppercase tracking-tighter flex items-center gap-1 mb-1">
                  <MapPin size={10}/> {t('userDetails.lblDefSite')}
              </label>
              <select 
                  className="outline-none py-1 font-black text-blue-900 bg-transparent uppercase text-[11px]"
                  value={userData.site_id}
                  onChange={e => setUserData({...userData, site_id: e.target.value})}
              >
                  <option value="">Select Site...</option>
                  {sites.map(site => <option key={site.site_id} value={site.site_id}>{site.description}</option>)}
              </select>
          </div>

          {/* 🎯 Secondary Roles (Multi-Select via Checkboxes) */}
          <div className="col-span-2 mt-4">
              <label className="text-[0.9em] font-black text-gray-400 uppercase tracking-tighter flex items-center gap-1 mb-2">
                  <Users size={12} className="text-gray-400"/> {t('userDetails.lblSecRoles')}
              </label>
              <div className="grid grid-cols-3 gap-2 bg-gray-50 p-3 rounded border border-gray-200 max-h-32 overflow-y-auto">
                  {roles.filter(r => r.role_id.toString() !== userData.role_id.toString()).map(role => (
                      <label key={role.role_id} className="flex items-center gap-2 cursor-pointer text-[1.0em] text-gray-700 hover:text-[#005495]">
                          <input 
                              type="checkbox" 
                              checked={userData.additional_roles.includes(role.role_id)}
                              onChange={() => toggleAdditionalRole(role.role_id)}
                              className="accent-[#005495]"
                          />
                          {role.name}
                      </label>
                  ))}
              </div>
          </div>
        </Section>

        {/* 3. Delegation Section 🎯 */}
        <Section title={t('userDetails.secDelegation')}>
          <div className="flex flex-col border-b border-gray-100 py-1">
            <label className="text-[0.9em] text-gray-400 italic font-black uppercase mb-1">{t('userDetails.lblDelegateTo')}</label>
            <div className="flex items-center gap-2">
              <User size={14} className="text-purple-500" />
              <select 
                value={userData.delegation.delegate_user_id} 
                onChange={e => setUserData({...userData, delegation: {...userData.delegation, delegate_user_id: e.target.value}})}
                className="w-full bg-transparent outline-none font-black text-blue-900 text-[12px] uppercase cursor-pointer"
              >
                <option value="">-- NO DELEGATION --</option>
                {allUsers.filter(u => u.user_id !== parseInt(id)).map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.username} - {u.full_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-12 mt-2">
              <InputField 
                  label={t('userDetails.lblStartDate')} 
                  type="datetime-local" 
                  value={userData.delegation.start_date} 
                  onChange={v => setUserData({...userData, delegation: {...userData.delegation, start_date: v}})} 
                  icon={CalendarClock}
                  disabled={!userData.delegation.delegate_user_id}
              />
              <InputField 
                  label={t('userDetails.lblEndDate')} 
                  type="datetime-local" 
                  value={userData.delegation.end_date} 
                  onChange={v => setUserData({...userData, delegation: {...userData.delegation, end_date: v}})} 
                  icon={CalendarClock}
                  disabled={!userData.delegation.delegate_user_id}
              />
          </div>
        </Section>
      </div>
    </div>
  );
};

export default UserDetails;